// ---------------------------------------------------------------------------
// A button that has to be held.
//
// It exists for one problem: on a phone the numbers round has `Add step` and
// `Lock in` a thumb's width apart, both full-width, both the primary blue, and
// one of them ends the round. Tapping the wrong one costs the player the rest of
// their working, and the two looked near enough identical that it happened.
//
// So this one is not tapped, it is held, and three things carry that:
//
//   1. **The gesture explains itself while it is happening.** A fill sweeps
//      left to right across the button for as long as the finger is down. A
//      stray tap starts the sweep, the sweep collapses, and nothing else
//      happens — which is a wrong press that teaches the right one rather than a
//      wrong press that scores.
//   2. **A different shape and a smaller footprint** than the button it is
//      being told apart from. That is the caller's to draw, and the caller does;
//      what is fixed here is that the surface is never the primary gradient.
//   3. **The keyboard does not hold anything.** Enter and Space fire on the
//      press, immediately. A hold gesture a keyboard cannot perform is not an
//      accessible control, and there is no such thing as a stray Enter on a
//      focused button the way there is a stray tap on a phone — the whole reason
//      for the hold does not exist on that path, so neither does the hold.
//
// **The fill is driven by requestAnimationFrame, not by a CSS transition, and
// that is not a preference.** `index.css` crushes `transition-duration` to a
// hundredth of a millisecond under reduced motion, so a transitioned bar would
// snap to full on the first frame and then sit there for the rest of the hold —
// a progress bar that lies about the progress, for exactly the players least
// able to tell. Writing the width per frame is immune to it, and one clock
// drives both the bar and the firing, so the bar reaching the end and the action
// landing cannot disagree.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

/**
 * How long the finger stays down. Long enough that a mis-tap cannot reach it,
 * short enough that a deliberate press does not feel like it has hung.
 */
export const HOLD_MS = 600;

export interface HoldButtonProps {
  /** Run once, when the hold completes or a keyboard press activates it. */
  onHold: () => void;
  disabled?: boolean;
  /**
   * Do not let this press move focus.
   *
   * For a hold that sits beside a focused text field on a phone, where taking
   * focus takes the keyboard down with it. The press still works; only the
   * focus shift is suppressed. Off by default, because a control that refuses
   * focus is the wrong default for a control people also reach by tabbing.
   */
  keepFocus?: boolean;
  /** The label. It should say that the control is held, because it is. */
  children: ReactNode;
  className?: string;
  /**
   * Spoken instead of the visible label where the label is short. The hold is
   * described here as well: a screen reader user on a phone has no way to see
   * the fill.
   */
  'aria-label'?: string;
  holdMs?: number;
}

export function HoldButton({
  onHold,
  disabled = false,
  keepFocus = false,
  children,
  className,
  holdMs = HOLD_MS,
  'aria-label': ariaLabel,
}: HoldButtonProps): ReactElement {
  const fillRef = useRef<HTMLSpanElement>(null);
  const frameRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  // Read by the frame loop, which must not be rebuilt every render just because
  // its caller passed a new closure.
  const onHoldRef = useRef(onHold);
  useEffect(() => {
    onHoldRef.current = onHold;
  });

  // Only so the label can say the press was heard. The bar itself never goes
  // through React: a setState per frame would re-render the whole numbers
  // screen sixty times a second for the length of the hold.
  const [holding, setHolding] = useState(false);

  const paint = (fraction: number) => {
    const fill = fillRef.current;
    if (fill !== null) fill.style.transform = `scaleX(${fraction})`;
  };

  const stop = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    startedAtRef.current = null;
    setHolding(false);
    paint(0);
  }, []);

  // A hold interrupted by the component going away leaves a frame request
  // behind, and the callback it runs would fire the action into an unmounted
  // tree.
  useEffect(() => stop, [stop]);

  const begin = () => {
    if (disabled || startedAtRef.current !== null) return;
    startedAtRef.current = performance.now();
    setHolding(true);

    const step = () => {
      const startedAt = startedAtRef.current;
      if (startedAt === null) return;
      const fraction = Math.min(1, (performance.now() - startedAt) / holdMs);
      paint(fraction);
      if (fraction < 1) {
        frameRef.current = requestAnimationFrame(step);
        return;
      }
      // Cleared before the call, so a handler that unmounts this button is not
      // racing the frame loop for the same refs.
      stop();
      onHoldRef.current();
    };

    frameRef.current = requestAnimationFrame(step);
  };

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      // A hold is invisible to anything that cannot see the fill, and the
      // shortcut is the honest description of the other way in.
      aria-keyshortcuts="Enter"
      data-holding={holding || undefined}
      onPointerDown={(event) => {
        // The primary button only, and never a second finger arriving mid-hold.
        if (event.button !== 0) return;
        // Before anything else, so the focus shift never happens rather than
        // being undone. See `keepFocus`.
        if (keepFocus) event.preventDefault();
        // So the pointer keeps reporting to this element even if the thumb
        // drifts off it, which is what a thumb does over 600ms.
        //
        // It is a nicety and it is wrapped like one. jsdom has no pointer
        // capture at all, and a browser throws for a pointer id it is not
        // currently tracking — either of which, unguarded, would throw out of
        // the handler BEFORE the hold starts and leave a button that cannot be
        // pressed at all. Losing the capture costs a hold that ends if the thumb
        // slides off; losing the hold costs the round.
        try {
          event.currentTarget.setPointerCapture?.(event.pointerId);
        } catch {
          // Nothing to do about it, and nothing that depends on it.
        }
        begin();
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
      onLostPointerCapture={stop}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space scrolls the page otherwise, and a key held down repeats.
        event.preventDefault();
        if (event.repeat || disabled) return;
        onHold();
      }}
      className={[
        'relative isolate flex items-center justify-center overflow-hidden',
        'font-bold whitespace-nowrap select-none',
        'transition-colors duration-150 ease-out disabled:opacity-40',
        // `touch-none` so the browser does not decide, part way through the
        // hold, that the finger is scrolling and take the pointer away.
        'touch-none',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* The fill. Behind the label and inside the button's own rounding, drawn
          as a scale rather than a width so every frame is a compositor job and
          none of them is a layout. `origin-left` is what makes it sweep from
          the left rather than growing out of the middle.

          A wash over the opaque surface, not a solid fill: `good` is a
          foreground colour in this palette and has never been solved as a
          background carrying text. */}
      <span
        ref={fillRef}
        aria-hidden="true"
        data-fill=""
        className="absolute inset-0 -z-10 origin-left bg-good/30"
        style={{ transform: 'scaleX(0)' }}
      />
      {children}
    </button>
  );
}
